import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Web Audio: tone generation (oscillator) + mic-based tone detection.
 * Detection runs in an AudioWorklet at sample rate (synchronous demod →
 * cascaded-IIR envelope → max-window state machine), which is
 * substantially more accurate than the previous Goertzel/RAF approach
 * for shaky-room conditions. See /app/test_rx for offline validation.
 */
export function useMorseAudio({ frequency, audioEnabled, sensitivity = 70, onToneEdge, onNoiseFloor }) {
  const ctxRef = useRef(null);
  const oscRef = useRef(null);
  const gainRef = useRef(null);

  const micStreamRef = useRef(null);
  const analyserRef = useRef(null);   // kept only for waitForMicSignal probe
  const workletNodeRef = useRef(null);
  const toneStateRef = useRef(false);
  const lastEdgeRef = useRef(0);
  // When true, skip mic edge detection (we're transmitting — avoid feedback).
  const muteDetectRef = useRef(false);
  const unmuteTimerRef = useRef(null);

  const [micActive, setMicActive] = useState(false);
  const [micLevel, setMicLevel] = useState(0); // 0..1 for visual meter
  const [micMagnitude, setMicMagnitude] = useState(0); // raw smoothed magnitude
  const [detectThreshold, setDetectThreshold] = useState(0.015); // ON threshold currently in use

  const sensitivityRef = useRef(sensitivity);
  const lastMagRef = useRef(0);
  // Noise-floor sampling state. While set, accumulates worklet `level`
  // messages (each is mean envelope over ~32 ms) for post-processing.
  const noiseSampleRef = useRef(null);
  const onNoiseFloorRef = useRef(onNoiseFloor);
  useEffect(() => { onNoiseFloorRef.current = onNoiseFloor; }, [onNoiseFloor]);
  // Calibration capture state: while true, store edge-derived pulse
  // durations and the peak envelope magnitude observed.
  const calibratingRef = useRef(false);
  const calibDataRef = useRef({ peak: 0, pulses: [], pulseStart: 0 });
  useEffect(() => { sensitivityRef.current = sensitivity; }, [sensitivity]);

  // iOS reports a non-standard "interrupted" state after calls/Siri/app
  // switches. Treat it like suspended and try to resume.
  const resumeCtx = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    if (ctx.state === "suspended" || ctx.state === "interrupted") {
      ctx.resume().catch(() => {});
    }
  }, []);

  const ensureCtx = useCallback(() => {
    if (ctxRef.current && ctxRef.current.state === "closed") {
      ctxRef.current = null; // iOS can wedge a context permanently — rebuild
    }
    if (!ctxRef.current) {
      const AC = window.AudioContext || window.webkitAudioContext;
      const ctx = new AC();
      // Auto-recover when iOS interrupts the session (call, Siri, lock).
      ctx.onstatechange = () => {
        if (ctx.state === "interrupted") ctx.resume().catch(() => {});
      };
      ctxRef.current = ctx;
    }
    resumeCtx();
    return ctxRef.current;
  }, [resumeCtx]);

  // iOS silent-switch workaround: playing a short HTML5 <audio> element flips
  // Safari's audio session to "playback", so Web Audio output follows the
  // volume buttons instead of being muted by the ringer switch.
  const silentAudioRef = useRef(null);
  const playSilentHtmlAudio = useCallback(() => {
    try {
      if (!silentAudioRef.current) {
        const a = new Audio(
          "data:audio/wav;base64,UklGRigAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAAABkYXRhAgAAAAEA"
        );
        a.setAttribute("playsinline", "");
        a.preload = "auto";
        silentAudioRef.current = a;
      }
      const p = silentAudioRef.current.play();
      if (p && p.catch) p.catch(() => {});
    } catch (_) {}
  }, []);

  // iOS/Safari: Web Audio will remain silent until a user gesture kicks a
  // real sound through `destination`. We create the context, play a tiny
  // silent buffer, AND play a silent <audio> element (silent-switch bypass).
  // Called on the Audio toggle gesture and re-run on every call so a wedged
  // session gets another chance to unlock.
  const unlockedRef = useRef(false);
  const unlockAudio = useCallback(() => {
    try {
      const ctx = ensureCtx();
      resumeCtx();
      playSilentHtmlAudio();
      if (!unlockedRef.current) {
        const buffer = ctx.createBuffer(1, 1, 22050);
        const source = ctx.createBufferSource();
        source.buffer = buffer;
        source.connect(ctx.destination);
        source.start(0);
        unlockedRef.current = true;
      }
    } catch (_) {
      // best-effort only
    }
  }, [ensureCtx, resumeCtx, playSilentHtmlAudio]);

  // Recover the context whenever the page returns to the foreground or the
  // user touches the screen. iOS often refuses background resume() calls but
  // always honors one made inside a real gesture, so both paths are needed.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") resumeCtx();
    };
    const onGesture = () => resumeCtx();
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", onVisible);
    window.addEventListener("focus", onVisible);
    document.addEventListener("touchend", onGesture, true);
    document.addEventListener("pointerdown", onGesture, true);
    return () => {
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", onVisible);
      window.removeEventListener("focus", onVisible);
      document.removeEventListener("touchend", onGesture, true);
      document.removeEventListener("pointerdown", onGesture, true);
    };
  }, [resumeCtx]);

  const startTone = useCallback(() => {
    if (!audioEnabled) return;
    const ctx = ensureCtx();
    // iOS: make sure context is really running before scheduling
    if (ctx.state !== "running") ctx.resume().catch(() => {});
    // stop previous
    if (oscRef.current) {
      try { oscRef.current.stop(); } catch (_) {}
      oscRef.current.disconnect();
      oscRef.current = null;
    }
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.value = frequency;
    // ADSR-ish: quick ramp up/down to avoid clicks
    gain.gain.setValueAtTime(0, ctx.currentTime);
    gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + 0.008);
    osc.connect(gain).connect(ctx.destination);
    osc.start();
    oscRef.current = osc;
    gainRef.current = gain;
  }, [audioEnabled, frequency, ensureCtx]);

  const stopTone = useCallback(() => {
    const ctx = ctxRef.current;
    if (!ctx || !oscRef.current) return;
    const osc = oscRef.current;
    const gain = gainRef.current;
    const now = ctx.currentTime;
    try {
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(0, now + 0.01);
      osc.stop(now + 0.02);
    } catch (_) {}
    oscRef.current = null;
    gainRef.current = null;
  }, []);

  // Update oscillator frequency live if pressed
  useEffect(() => {
    if (oscRef.current && ctxRef.current) {
      oscRef.current.frequency.setValueAtTime(frequency, ctxRef.current.currentTime);
    }
  }, [frequency]);

  // --- Mic detection (Goertzel on time-domain samples) ---
  // Polls the analyser's time-domain buffer until peak |sample| crosses
  // a tiny floor (i.e. real audio is flowing, not all-zeros). Returns
  // when signal has been seen, or after a hard timeout. Required because
  // `getUserMedia` resolves before audio actually starts pumping on some
  // devices, and a noise-floor measurement on silence yields p95 ≈ 0
  // which the inverse-sensitivity map pegs at 100%.
  const waitForMicSignal = useCallback((maxWaitMs = 2500) => {
    return new Promise((resolve) => {
      const start = performance.now();
      const probe = new Float32Array(2048);
      const tick = () => {
        const a = analyserRef.current;
        if (!a) return resolve();
        a.getFloatTimeDomainData(probe);
        let peak = 0;
        // Sparse scan is plenty — we just need to know audio is flowing.
        for (let i = 0; i < probe.length; i += 16) {
          const v = Math.abs(probe[i]);
          if (v > peak) peak = v;
        }
        if (peak > 1e-5 || performance.now() - start > maxWaitMs) {
          // Add a 200ms settle so the first noisy frames don't bias p95.
          setTimeout(resolve, 200);
        } else {
          setTimeout(tick, 60);
        }
      };
      tick();
    });
  }, []);

  const startMic = useCallback(async () => {
    if (micStreamRef.current) return;
    const ctx = ensureCtx();
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
      });
      micStreamRef.current = stream;
      const src = ctx.createMediaStreamSource(stream);
      // Analyser is kept only to probe for "audio actually flowing" in
      // waitForMicSignal. The worklet handles all detection.
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0;
      src.connect(analyser);
      analyserRef.current = analyser;
      // Load and instantiate the morse-detector AudioWorklet. Module is
      // served from /public so CRA hosts it at the app root.
      try {
        await ctx.audioWorklet.addModule("/morse-detector-worklet.js");
      } catch (e) {
        // addModule throws if already loaded — harmless.
        if (!String(e).includes("already")) {
          console.warn("worklet load issue:", e);
        }
      }
      const node = new AudioWorkletNode(ctx, "morse-detector", {
        numberOfInputs: 1,
        numberOfOutputs: 0,
        channelCount: 1,
      });
      node.port.onmessage = (ev) => handleWorkletMessage(ev.data);
      node.port.postMessage({
        type: "config",
        freq: freqRef.current,
        sensitivity: sensitivityRef.current,
      });
      src.connect(node);
      workletNodeRef.current = node;
      setMicActive(true);
      // Auto noise-floor: wait until the mic stream actually delivers
      // non-trivial samples before measuring.
      waitForMicSignal().then(() => {
        if (onNoiseFloorRef.current && workletNodeRef.current) {
          measureNoiseFloorInternal(900);
        }
      });
    } catch (err) {
      console.error("Mic access denied", err);
      setMicActive(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ensureCtx]);

  const stopMic = useCallback(() => {
    if (workletNodeRef.current) {
      try { workletNodeRef.current.port.close(); } catch (_) {}
      try { workletNodeRef.current.disconnect(); } catch (_) {}
      workletNodeRef.current = null;
    }
    if (micStreamRef.current) {
      micStreamRef.current.getTracks().forEach((t) => t.stop());
      micStreamRef.current = null;
    }
    analyserRef.current = null;
    setMicActive(false);
    setMicLevel(0);
    if (toneStateRef.current && onToneEdge) {
      toneStateRef.current = false;
      onToneEdge(false, performance.now());
    }
  }, [onToneEdge]);

  const freqRef = useRef(frequency);
  useEffect(() => { freqRef.current = frequency; }, [frequency]);

  const onEdgeRef = useRef(onToneEdge);
  useEffect(() => { onEdgeRef.current = onToneEdge; }, [onToneEdge]);

  // Push freq + sensitivity to the worklet whenever they change.
  useEffect(() => {
    if (workletNodeRef.current) {
      workletNodeRef.current.port.postMessage({
        type: "config",
        freq: frequency,
        sensitivity,
      });
    }
  }, [frequency, sensitivity]);

  // Translate worklet messages into hook callbacks + state.
  // - "edge"  → call onToneEdge (and update calibration if recording)
  // - "level" → set micMagnitude / micLevel for the meter; also feed
  //              noise-floor measurement and calibration peak.
  function handleWorkletMessage(msg) {
    if (!msg) return;
    if (msg.type === "edge") {
      const tMs = msg.t * 1000; // worklet sends seconds (currentTime)
      const on = !!msg.on;
      // Calibration capture.
      if (calibratingRef.current) {
        if (on) {
          calibDataRef.current.pulseStart = tMs;
        } else if (calibDataRef.current.pulseStart > 0) {
          const dur = tMs - calibDataRef.current.pulseStart;
          if (dur > 20 && dur < 4000) {
            calibDataRef.current.pulses.push(dur);
          }
          calibDataRef.current.pulseStart = 0;
        }
      }
      toneStateRef.current = on;
      lastEdgeRef.current = tMs;
      if (onEdgeRef.current) onEdgeRef.current(on, tMs);
    } else if (msg.type === "level") {
      const mag = msg.mag || 0;
      const peak = msg.peak || 0;
      lastMagRef.current = mag;
      setMicMagnitude(mag);
      setMicLevel(Math.min(1, Math.sqrt(mag / 0.05)));
      if (typeof msg.onThr === "number") setDetectThreshold(msg.onThr);
      if (noiseSampleRef.current) {
        // Use the per-window PEAK (not mean) so transient noise spikes
        // like HVAC clicks bump the noise statistics appropriately.
        // Using `mag` would hide spikes in the 32 ms average and
        // produce a threshold that only rejects steady noise.
        noiseSampleRef.current.samples.push(peak);
      }
      if (calibratingRef.current && peak > calibDataRef.current.peak) {
        calibDataRef.current.peak = peak;
      }
    }
  }


  useEffect(() => {
    return () => {
      stopTone();
      stopMic();
      if (ctxRef.current) {
        try { ctxRef.current.close(); } catch (_) {}
        ctxRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Suspend mic edge detection while the local device is transmitting so the
  // mic doesn't pick up its own speaker output. `grace` extends the mute for
  // a short tail after stopping so the speaker's decay isn't decoded.
  const setTransmitting = useCallback((on, grace = 250) => {
    if (unmuteTimerRef.current) {
      clearTimeout(unmuteTimerRef.current);
      unmuteTimerRef.current = null;
    }
    const node = workletNodeRef.current;
    if (on) {
      muteDetectRef.current = true;
      if (node) node.port.postMessage({ type: "mute", muted: true });
    } else {
      unmuteTimerRef.current = setTimeout(() => {
        muteDetectRef.current = false;
        if (workletNodeRef.current) {
          workletNodeRef.current.port.postMessage({ type: "mute", muted: false });
        }
        unmuteTimerRef.current = null;
      }, grace);
    }
  }, []);

  // Listens to the mic for `durationMs`, returns peak magnitude AND any
  // pulse durations observed in that window. Pulses let the caller auto-tune
  // WPM from the sender's keying speed without a separate calibration step.
  const captureMaxMagnitude = useCallback(async (durationMs = 3000) => {
    if (!workletNodeRef.current) return { peak: 0, pulses: [] };
    calibDataRef.current = { peak: 0, pulses: [], pulseStart: 0 };
    calibratingRef.current = true;
    await new Promise((r) => setTimeout(r, durationMs));
    calibratingRef.current = false;
    return {
      peak: calibDataRef.current.peak,
      pulses: [...calibDataRef.current.pulses],
    };
  }, []);

  // Convert a target ON threshold back into the matching sensitivity slider
  // value (inverse of the exponential map used in the detection loop).
  const sensitivityForThreshold = useCallback((threshold) => {
    const t = Math.max(0.00005, Math.min(0.2, threshold));
    return Math.max(0, Math.min(100, (-Math.log(t / 0.2) / 7.5) * 100));
  }, []);

  // Measure ambient noise-floor magnitude over `durationMs` and call
  // `onNoiseFloor` with { p95, mean, max, std, suggestedSensitivity, valid }.
  //
  // Threshold = max(CFAR_gate, observed_max) × SAFETY_MULT.
  // First term picks the worst-case noise estimate — whichever of
  // (mean + K·std) and (observed max-peak) is larger. Then SAFETY_MULT
  // lifts the threshold to "N times above the noise floor", matching
  // the visual margin users expect.
  //   - K_STD=6 → Gaussian false-alarm ≈ 10⁻⁹, effectively zero
  //   - SAFETY_MULT=6 → threshold at ~6x above worst noise, ~36x
  //     above the typical sample → level-bar display has clear gap
  // Empirically tuned against a quiet room reporting floor ≈ 1.2‰
  // where the previous formula (max + 10·std) landed too close.
  const NOISE_K_STD = 6;
  const NOISE_SAFETY_MULT = 6;
  const NOISE_FLOOR_MIN = 0.00005;
  const SENS_CEILING = 92;
  const measureNoiseFloorInternal = useCallback((durationMs = 900) => {
    if (!workletNodeRef.current) return;
    noiseSampleRef.current = { samples: [] };
    setTimeout(() => {
      const rec = noiseSampleRef.current;
      noiseSampleRef.current = null;
      if (!rec || rec.samples.length < 10) return;
      const samples = rec.samples;
      const sorted = [...samples].sort((a, b) => a - b);
      const p95 = sorted[Math.floor(sorted.length * 0.95)];
      const max = sorted[sorted.length - 1];
      let mean = 0;
      for (const v of samples) mean += v;
      mean /= samples.length;
      let varAcc = 0;
      for (const v of samples) {
        const d = v - mean;
        varAcc += d * d;
      }
      const std = Math.sqrt(varAcc / samples.length);
      const worstNoise = Math.max(mean + NOISE_K_STD * std, max);
      const target = worstNoise * NOISE_SAFETY_MULT;
      const valid = target >= NOISE_FLOOR_MIN * 2;
      const sug = Math.round(Math.max(0, Math.min(SENS_CEILING,
        (-Math.log(Math.max(target, NOISE_FLOOR_MIN) / 0.2) / 7.5) * 100)));
      if (onNoiseFloorRef.current) {
        onNoiseFloorRef.current({
          p95, mean, max, std, valid,
          suggestedSensitivity: valid ? sug : null,
        });
      }
    }, durationMs);
  }, []);

  // Public: re-run the noise-floor measurement (e.g. when user taps a
  // "recalibrate noise" button).
  const measureNoiseFloor = useCallback((durationMs = 900) => {
    measureNoiseFloorInternal(durationMs);
  }, [measureNoiseFloorInternal]);

  // Capture the duration of the next single ON->OFF pulse received within
  // `timeoutMs`. Returns { duration, peak } or null on timeout.
  const captureNextPulse = useCallback(async (timeoutMs = 10000) => {
    if (!workletNodeRef.current) return null;
    calibDataRef.current = { peak: 0, pulses: [], pulseStart: 0 };
    calibratingRef.current = true;
    const start = performance.now();
    while (performance.now() - start < timeoutMs) {
      if (calibDataRef.current.pulses.length > 0) {
        const duration = calibDataRef.current.pulses[0];
        const peak = calibDataRef.current.peak;
        calibratingRef.current = false;
        return { duration, peak };
      }
      await new Promise((r) => setTimeout(r, 30));
    }
    calibratingRef.current = false;
    return null;
  }, []);


  return {
    startTone,
    stopTone,
    startMic,
    stopMic,
    setTransmitting,
    unlockAudio,
    captureMaxMagnitude,
    captureNextPulse,
    sensitivityForThreshold,
    measureNoiseFloor,
    micActive,
    micLevel,
    micMagnitude,
    detectThreshold,
  };
}
